import { FlexBox, Text } from '@dixa/lib-shared-habla';

import {
  KnowledgeAiDraftSettings,
  KnowledgeAiDraftSimilarity,
} from 'apis/auditLogs';
import { AppLink } from 'components/appLink';

import { ConversationLink } from '../userActivityTypes/conversationLink/conversationLink';
import { DetailField, hasValue } from '../userActivityTypes/detailField';

const similarityLabels: Record<KnowledgeAiDraftSimilarity, string> = {
  BROAD: 'Broad',
  DEFAULT: 'Default',
  STRICT: 'Strict',
};

type AiDraftActivityDetailsProps = {
  /** What the content was asked to say. */
  readonly feedback?: string | null;
  /** Labels the feedback: an agent's, or the gap the AI found. */
  readonly feedbackLabel: string;
  readonly settings?: KnowledgeAiDraftSettings | null;
  readonly conversationId?: number | null;
  readonly similarConversationIds?: readonly number[] | null;
};

/**
 * The feedback, conversations and AI settings behind a change, shown when it
 * is expanded.
 */
export const AiDraftActivityDetails = ({
  feedback,
  feedbackLabel,
  settings,
  conversationId,
  similarConversationIds,
}: AiDraftActivityDetailsProps): JSX.Element => {
  const conversationFields: DetailField[] = [
    {
      label: 'Initiated from conversation',
      value: conversationId && (
        <ConversationLink conversationId={`${conversationId}`}>
          #{conversationId}
        </ConversationLink>
      ),
    },
    {
      label: 'Found similar conversations',
      value: similarConversationIds?.length ? (
        <FlexBox gap="100" flexWrap="wrap">
          {similarConversationIds.map((csid) => (
            <ConversationLink key={csid} conversationId={`${csid}`}>
              #{csid}
            </ConversationLink>
          ))}
        </FlexBox>
      ) : undefined,
    },
  ];

  const settingsFields: DetailField[] = settings
    ? [
        {
          label: 'Settings version',
          // The defaults and an unsaved draft of the settings have no page.
          value: settings.id ? (
            <AppLink
              path="/knowledge/settings/ai-suggestions/versions/:versionId"
              params={{ path: { versionId: settings.id } }}
            >
              {settings.name}
            </AppLink>
          ) : (
            settings.name
          ),
        },
        {
          label: 'Similarity',
          // A level this client does not know yet is shown as it arrives.
          value:
            similarityLabels[
              settings.similarity as KnowledgeAiDraftSimilarity
            ] ?? settings.similarity,
        },
        {
          label: 'Minimum similar conversations',
          value: settings.min_similar_conversations,
        },
        {
          label: 'Instructions for finding gaps',
          value: settings.judge_instructions,
        },
        {
          label: 'Instructions for writing drafts',
          value: settings.writer_instructions,
        },
      ]
    : [];
  const fields = [...conversationFields, ...settingsFields].filter(hasValue);

  return (
    <FlexBox margin="200 0 0 500" gap="300" flexDirection="column">
      {feedback && (
        <FlexBox flexDirection="column" gap="100">
          <Text.Paragraph as="span" variant="4" subdued>
            {feedbackLabel}
          </Text.Paragraph>
          <Text.Paragraph variant="3">{feedback}</Text.Paragraph>
        </FlexBox>
      )}
      {fields.length > 0 && (
        <FlexBox flexDirection="column" gap="200">
          {fields.map((field) => (
            <DetailField key={field.label} {...field} />
          ))}
        </FlexBox>
      )}
    </FlexBox>
  );
};
